import React, { useState, useEffect } from 'react'
import {
  Modal,
  Button,
  Input,
  Avatar,
  Select,
  Empty,
  Spin,
  Tooltip,
  message
} from 'antd'
import {
  DownOutlined,
  UpOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  DeleteOutlined,
  UserOutlined
} from '@ant-design/icons'
import { api } from '../../../../../services'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'

const accessOptions = [
  { label: 'View Access', value: 'View Access' },
  { label: 'Edit Access', value: 'Edit Access' }
]

const getPersonDisplay = (permission, users) => {
  // Find user details from users array
  const user = users.find(u => u.id === permission.user_id)

  // Get name from users array, fallback to email if no name
  let name = ''
  if (user) {
    if (user.first_name && user.last_name) {
      name = `${user.first_name} ${user.last_name}`
    } else if (user.first_name) {
      name = user.first_name
    } else if (user.last_name) {
      name = user.last_name
    } else if (user.name) {
      name = user.name
    }
  }

  // Email is mandatory - use from users array or fallback to permission data
  const email =
    user && user.email
      ? user.email
      : permission.user && permission.user.email
        ? permission.user.email
        : 'Unknown Email'

  // If no name found, use email as display name
  const displayName = name || email

  // Avatar: always use from permissions response
  let avatarUrl = ''
  if (
    permission.user &&
    permission.user.avatar &&
    permission.user.avatar.link
  ) {
    avatarUrl = permission.user.avatar.link
  }

  return {
    id: permission.id,
    name: displayName,
    avatar: avatarUrl,
    access: permission.access_type === 'edit' ? 'Edit Access' : 'View Access',
    email
  }
}

const ManagePermissionsModal = ({
  orgUserList = [],
  visible,
  onClose,
  id = null,
  refreshData
}) => {
  const [people, setPeople] = useState([])
  const [groups, setGroups] = useState([])
  const [allGroupList, setAllGroupList] = useState([])
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedPeople, setSelectedPeople] = useState([])
  const [expandedPeople, setExpandedPeople] = useState(true)
  const [expandedGroups, setExpandedGroups] = useState(true)
  const [search, setSearch] = useState('')
  const [addLoading, setAddLoading] = useState(false)
  const allOrgGroups = useSelector(state => state.organizations.list)
  const [orgUsers, setOrgUsers] = useState([])
  const [permissionsLoading, setPermissionsLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [deletingPermissions, setDeletingPermissions] = useState(new Set())
  const [deleteModalVisible, setDeleteModalVisible] = useState(false)
  const [permissionToDelete, setPermissionToDelete] = useState(null)
  const [groupUsersModalVisible, setGroupUsersModalVisible] = useState(false)
  const [selectedGroupUsers, setSelectedGroupUsers] = useState([])
  const [selectedGroupTitle, setSelectedGroupTitle] = useState('')

  const user = useSelector(state => state.user)
  const activeOrganizationId =
    user && user.info && user.info.organization && user.info.organization.id

  // Debug modal visibility
  useEffect(() => {
    // console.log('deleteModalVisible changed to:', deleteModalVisible)
  }, [deleteModalVisible])

  useEffect(() => {
    if (visible && id) {
      fetchPermissions(id)
    }
  }, [visible, id])

  useEffect(() => {
    if (
      allOrgGroups &&
      allOrgGroups.rows &&
      allOrgGroups.rows.length > 0 &&
      activeOrganizationId
    ) {
      let allGroup = []
      allOrgGroups.rows.forEach(el => {
        if (el.groups && activeOrganizationId === el.id) {
          allGroup.push(...el.groups)
        }
      })
      setAllGroupList(allGroup)
    }
  }, [allOrgGroups, activeOrganizationId])
  useEffect(() => {
    if (orgUserList.length > 0) {
      setOrgUsers(orgUserList)
    }
  }, [orgUserList])

  // Clear data when modal opens/closes
  useEffect(() => {
    if (!visible) {
      clearAllData()
    }
  }, [visible])

  const fetchPermissions = async docId => {
    setPermissionsLoading(true)
    try {
      const peopleRes = await api.library.getAllPeoplePermissions(docId)
      const groupsRes = await api.library.getAllGroupPermissions(docId)

      // Map people permissions to include name and avatar
      const availableUsers = orgUsers || []
      setPeople(
        (peopleRes.data || []).map(p => getPersonDisplay(p, availableUsers))
      )
      setGroups(
        (groupsRes.data || []).map(p => ({
          ...p,
          id: p.id || p.group_id, // Ensure id field exists
          group_id: p.group_id || p.id, // Ensure group_id field exists
          access: p.access_type === 'edit' ? 'Edit Access' : 'View Access'
        }))
      )
    } catch (error) {
      console.error('Error fetching permissions:', error)
      setPeople([])
      setGroups([])
    } finally {
      setPermissionsLoading(false)
    }
  }
  const { t } = useTranslation()
  const handlePersonAccessChange = (value, idx) => {
    setPeople(prev =>
      prev.map((p, i) => (i === idx ? { ...p, access: value } : p))
    )
  }

  const handleGroupAccessChange = (value, idx) => {
    setGroups(prev =>
      prev.map((g, i) => (i === idx ? { ...g, access: value } : g))
    )
  }

  const handleRemovePerson = (idx, isNew) => {
    if (isNew) {
      setPeople(prev => prev.filter((_, i) => i !== idx))
      return
    }
    const person = people[idx]
    setPermissionToDelete({ type: 'person', index: idx, data: person })
    setDeleteModalVisible(true)
  }

  const handleRemoveGroup = (idx, isNew) => {
    if (isNew) {
      setGroups(prev => prev.filter((_, i) => i !== idx))
      return
    }
    const group = groups[idx]
    setPermissionToDelete({ type: 'group', index: idx, data: group })
    setDeleteModalVisible(true)
  }

  const handleConfirmDelete = async () => {
    if (!permissionToDelete || !id) return

    const { type, index, data } = permissionToDelete

    // Try different possible ID fields
    const permissionId =
      data.id || data.permission_id || data.user_id || data.group_id

    // Add to deleting set
    setDeletingPermissions(prev => new Set(prev).add(permissionId))
    try {
      await api.library.deletePermission(id, permissionId)

      // Remove from the appropriate list
      if (type === 'person') {
        setPeople(prev => prev.filter((_, i) => i !== index))
      } else {
        setGroups(prev => prev.filter((_, i) => i !== index))
      }

      message.success(
        `${type === 'person' ? 'Person' : 'Group'} removed successfully`
      )
    } catch (error) {
      console.error('Error deleting permission:', error)
      message.error('Failed to remove permission')
    } finally {
      // Remove from deleting set
      setDeletingPermissions(prev => {
        const newSet = new Set(prev)
        newSet.delete(permissionId)
        return newSet
      })
      setDeleteModalVisible(false)
      setPermissionToDelete(null)
    }
  }

  const handleCancelDelete = () => {
    setDeleteModalVisible(false)
    setPermissionToDelete(null)
  }

  const handleGroupClick = group => {
    // Extract users from the group data structure
    const users = group.group && Array.isArray(group.group) ? group.group : []
    setSelectedGroupUsers(users)
    setSelectedGroupTitle(getGroupTitle(group.group_id))
    setGroupUsersModalVisible(true)
  }

  const handleCloseGroupUsersModal = () => {
    setGroupUsersModalVisible(false)
    setSelectedGroupUsers([])
    setSelectedGroupTitle('')
  }

  // Temporarily disable search filtering to debug
  const filteredPeople = people

  const handleAddGroups = async () => {
    if (!id || !selectedGroups || selectedGroups.length === 0) return
    setAddLoading(true)

    // Filter out groups that are already added - check both id and group_id fields
    const existingGroupIds = groups.map(g => g.id || g.group_id)
    const newGroupIds = selectedGroups.filter(
      groupId => !existingGroupIds.includes(groupId)
    )

    // Add selected groups to the groups list with default 'View Access'
    const newGroups = newGroupIds.map(groupId => {
      const group = allGroupList.find(g => g.id === groupId)
      return {
        id: groupId,
        group_id: groupId,
        title: group ? group.title : `Group ${groupId}`,
        access: 'View Access',
        isNew: true
      }
    })

    setGroups(prev => [...prev, ...newGroups])
    setSelectedGroups([]) // Clear selection
    setAddLoading(false)
  }

  const handleAddPeople = async () => {
    if (!id || !selectedPeople || selectedPeople.length === 0) return
    setAddLoading(true) // Filter out people that are already added
    const existingPeopleIds = people.map(p => p.id)

    const newPeopleIds = selectedPeople.filter(
      userId => !existingPeopleIds.includes(userId)
    )

    // Add selected people to the people list with default 'View Access'
    const newPeople = newPeopleIds
      .map(userId => {
        let orgsUser = availableUsers.find(u => u.user_id === userId)
        const orgUser = orgsUser.user
        if (!orgUser) return null
        // Handle the new data structure (res.data.user)
        const userName =
          orgUser.first_name && orgUser.last_name
            ? `${orgUser.first_name} ${orgUser.last_name}`
            : orgUser.first_name ||
            orgUser.last_name ||
            orgUser.email ||
            orgUser.name

        const userEmail = orgUser.email
        const userAvatar = orgUser.avatar

        return {
          id: userId,
          name: userName,
          email: userEmail,
          avatar: userAvatar,
          access: 'View Access',
          isNew: true
        }
      })
      .filter(Boolean) // Remove any null entries

    setPeople(prev => {
      const updatedPeople = [...prev, ...newPeople]
      return updatedPeople
    })
    setSelectedPeople([]) // Clear selection
    setAddLoading(false)
  }

  const clearAllData = () => {
    setSelectedPeople([])
    setSelectedGroups([])
    setSearch('')
    setExpandedPeople(true)
    setExpandedGroups(true)
    setGroupUsersModalVisible(false)
    setSelectedGroupUsers([])
    setSelectedGroupTitle('')
  }

  const handleModalClose = () => {
    clearAllData()
    onClose()
  }

  const handleSavePermissions = async () => {
    setSaveLoading(true)
    try {
      // Group people by access type
      const editEmails = people
        .filter(p => p.access === 'Edit Access')
        .map(p => p.email)
      const viewEmails = people
        .filter(p => p.access === 'View Access')
        .map(p => p.email)
      // Group groups by access type
      const groupViewIds = groups
        .filter(g => g.access === 'View Access')
        .map(g => g.id)
      const groupEditIds = groups
        .filter(g => g.access === 'Edit Access')
        .map(g => g.id)

      const permission = []

      // Create view access object with both emails and group_ids if they exist
      const viewObject = { access_type: 'view' }
      if (viewEmails.length > 0) {
        viewObject.emails = viewEmails
      }
      if (groupViewIds.length > 0) {
        viewObject.group_ids = groupViewIds
      }
      if (viewEmails.length > 0 || groupViewIds.length > 0) {
        permission.push(viewObject)
      }

      // Create edit access object with both emails and group_ids if they exist
      const editObject = { access_type: 'edit' }
      if (editEmails.length > 0) {
        editObject.emails = editEmails
      }
      if (groupEditIds.length > 0) {
        editObject.group_ids = groupEditIds
      }
      if (editEmails.length > 0 || groupEditIds.length > 0) {
        permission.push(editObject)
      }

      const payload = { permission }
      await api.library.CreateUpdatePermissions(id, payload)
      refreshData()
      handleModalClose()
      setSaveLoading(false)
    } catch (error) {
      setSaveLoading(false)
    }
  }

  // Helper function to get display name for user
  const getUserDisplayName = user => {
    user = user.user
    // Handle the new data structure (res.data.user)
    if (user && user.email) {
      const firstName = user.first_name || ''
      const lastName = user.last_name || ''
      const name = user.name || ''

      if (firstName && lastName) {
        return `${firstName} ${lastName} (${user.email})`
      } else if (firstName) {
        return `${firstName} (${user.email})`
      } else if (lastName) {
        return `${lastName} (${user.email})`
      } else if (name) {
        return `${name} (${user.email})`
      } else {
        return user.email
      }
    }

    return (user && user.name) || 'Unknown User'
  }

  // Filter out already added users and groups
  const availableUsers = ((orgUsers.length > 0 && orgUsers) || []).filter(
    user => !people.some(p => p.id === user.user_id)
  )
  const availableGroups = allGroupList.filter(
    group => !groups.some(g => (g.id || g.group_id) === group.id)
  )
  // console.log('test availableGroups', availableGroups)
  // console.log('allGroupList:', allGroupList)
  // console.log('groups:', groups)
  // console.log('availableGroups filtering:', allGroupList.map(group => ({
  //   groupId: group.id,
  //   groupTitle: group.title,
  //   isFilteredOut: groups.some(g => g.id === group.id)
  // })))

  const getGroupTitle = id => {
    const groupData = availableGroups.find(el => el.id === id)

    // If not found in availableGroups, try allGroupList
    if (!groupData) {
      const allGroupData = allGroupList.find(el => el.id === id)
      return allGroupData && allGroupData.title
    }

    return groupData && groupData.title
  }
  return (
    <Modal
      title="Permissions"
      visible={visible}
      onCancel={handleModalClose}
      footer={null}
      width={600}
      bodyStyle={{ padding: 0 }}
      style={{ top: 40 }}
      centered
    >
      <div style={{ padding: 24 }}>
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 16
          }}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder="Select people"
            value={selectedPeople}
            onChange={values => {
              setSelectedPeople(values)
            }}
            style={{
              flex: 2,
              minWidth: 200,
              borderRadius: '8px'
            }}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              availableUsers.length === 0 ? (
                <Spin size="small" />
              ) : (
                t('v5:no_users_found')
              )
            }
          >
            {availableUsers.length === 0 ? (
              <Select.Option key="no-users" value="no-users" disabled>
                {t('v5:no_users_available')}
              </Select.Option>
            ) : (
              availableUsers.map(user => {
                const displayName = getUserDisplayName(user)
                return (
                  <Select.Option key={user.user_id} value={user.user_id}>
                    {displayName}
                  </Select.Option>
                )
              })
            )}
          </Select>
          <Button
            type="default"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleAddPeople}
            loading={addLoading}
            disabled={!selectedPeople.length}
          >
            + {t('v5:add_people')}
          </Button>
        </div>

        {/* People Section */}
        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 8,
            overflow: 'hidden',
            marginBottom: 16
          }}
        >
          <div
            style={{
              display: 'flex',
              background: '#fafbfc',
              fontWeight: 500,
              padding: '12px 24px',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setExpandedPeople(e => !e)}
          >
            <div style={{ flex: 1 }}>{t('v5:people')}</div>
            <div style={{ width: 160 }}>{t('v5:access_type')}</div>
            <div style={{ marginLeft: 8 }}>
              {expandedPeople ? <UpOutlined /> : <DownOutlined />}
            </div>
          </div>
          {expandedPeople &&
            (permissionsLoading ? (
              <div style={{ padding: '32px 0', textAlign: 'center' }}>
                <Spin />
              </div>
            ) : filteredPeople.length > 0 ? (
              filteredPeople.map((person, idx) => (
                <div
                  key={person.id || person.name + idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 24px',
                    borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12
                    }}
                  >
                    <Avatar
                      style={{ background: '#eee', color: '#333' }}
                      src={person.avatar || undefined}
                    >
                      {!person.avatar && person.name
                        ? person.name
                          .split(' ')
                          .map(n => n[0])
                          .join('')
                        : ''}
                    </Avatar>
                    <Tooltip title={person.email} placement="top">
                      <span style={{ fontWeight: 500, cursor: 'pointer' }}>
                        {person.name || person.email}
                      </span>
                    </Tooltip>
                  </div>
                  <div style={{ width: 160 }}>
                    <Select
                      value={person.access}
                      style={{ width: '100%' }}
                      onChange={value => handlePersonAccessChange(value, idx)}
                    >
                      {accessOptions.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginLeft: 8 }}>
                    <Button
                      type="text"
                      icon="delete"
                      onClick={() => {
                        handleRemovePerson(idx, person.isNew)
                      }}
                      style={{ color: '#ff4d4f' }}
                      size="small"
                      loading={deletingPermissions.has(person.id)}
                      disabled={deletingPermissions.has(person.id)}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '32px 0', textAlign: 'center' }}>
                <Empty description="No people found" />
              </div>
            ))}
        </div>

        {/* Groups Section */}
        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
            marginBottom: 16
          }}
        >
          <Select
            mode="multiple"
            showSearch
            placeholder="Select groups"
            value={selectedGroups}
            onChange={setSelectedGroups}
            style={{
              flex: 2,
              minWidth: 200,
              borderRadius: '8px'
            }}
            filterOption={(input, option) =>
              option.props.children
                .toLowerCase()
                .indexOf(input.toLowerCase()) >= 0
            }
            notFoundContent={
              availableGroups.length === 0 ? (
                <Spin size="small" />
              ) : (
                t('v5:no_group_found')
              )
            }
          >
            {availableGroups.map(group => (
              <Select.Option key={group.id} value={group.id}>
                {group.title}
              </Select.Option>
            ))}
          </Select>
          <Button
            type="default"
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            onClick={handleAddGroups}
            loading={addLoading}
            disabled={!selectedGroups.length}
          >
            + {t('v5:add_group')}
          </Button>
        </div>

        <div
          style={{
            border: '1px solid #eee',
            borderRadius: 8,
            overflow: 'hidden',
            marginBottom: 16
          }}
        >
          <div
            style={{
              display: 'flex',
              background: '#fafbfc',
              fontWeight: 500,
              padding: '12px 24px',
              alignItems: 'center',
              cursor: 'pointer'
            }}
            onClick={() => setExpandedGroups(e => !e)}
          >
            <div style={{ flex: 1 }}>{t('v5:groups')}</div>
            <div style={{ width: 160 }}>{t('v5:access_type')}</div>
            <div style={{ marginLeft: 8 }}>
              {expandedGroups ? <UpOutlined /> : <DownOutlined />}
            </div>
          </div>
          {expandedGroups &&
            (permissionsLoading ? (
              <div style={{ padding: '32px 0', textAlign: 'center' }}>
                <Spin />
              </div>
            ) : groups.length > 0 ? (
              groups.map((group, idx) => (
                <div
                  key={group.id || group.group_id || group.title + idx}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '16px 24px',
                    borderTop: idx === 0 ? 'none' : '1px solid #f0f0f0'
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      cursor: 'pointer',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                      padding: '4px 8px',
                      borderRadius: '4px',
                      transition: 'background-color 0.2s'
                    }}
                    onClick={() =>
                      group.group &&
                      Array.isArray(group.group) &&
                      handleGroupClick(group)
                    }
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = 'transparent'
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>
                      {getGroupTitle(group.group_id)}
                    </div>
                    {group.group && Array.isArray(group.group) && (
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#666',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 4
                        }}
                      >
                        <UserOutlined style={{ fontSize: '12px' }} />
                        {group.group.length}
                        {t('v5:users_top')}
                      </div>
                    )}
                  </div>
                  <div style={{ width: 160 }}>
                    <Select
                      value={group.access}
                      style={{ width: '100%' }}
                      onChange={value => handleGroupAccessChange(value, idx)}
                    >
                      {accessOptions.map(opt => (
                        <Select.Option key={opt.value} value={opt.value}>
                          {opt.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </div>
                  <div style={{ marginLeft: 8 }}>
                    <Button
                      type="text"
                      icon="delete"
                      onClick={() => {
                        handleRemoveGroup(idx, group.isNew)
                      }}
                      style={{ color: '#ff4d4f' }}
                      size="small"
                      loading={deletingPermissions.has(
                        group.id || group.group_id
                      )}
                      disabled={deletingPermissions.has(
                        group.id || group.group_id
                      )}
                    />
                  </div>
                </div>
              ))
            ) : (
              <div style={{ padding: '32px 0', textAlign: 'center' }}>
                <Empty description={t('v5:no_group_found')} />
              </div>
            ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
          <Button
            onClick={handleModalClose}
            style={{ color: '#7b7b93', background: 'none', border: 'none' }}
          >
            {t('v4:discard')}
          </Button>
          <Button
            type="primary"
            style={{ minWidth: 80 }}
            onClick={handleSavePermissions}
            loading={saveLoading}
            disabled={people.length === 0 && groups.length === 0}
          >
            {t('general:save')}
          </Button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        centered
        title={t('v5:confirm_delete')}
        visible={deleteModalVisible}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        okText="Delete"
        cancelText={t('v4:cancel')}
        okButtonProps={{ danger: true }}
        confirmLoading={
          permissionToDelete &&
          deletingPermissions.has(permissionToDelete.data.id)
        }
      >
        <p>
          {t('v5:do_you_want_remove')}{' '}
          <strong>{permissionToDelete && permissionToDelete.data.name}</strong>{' '}
          {t('v5:form_this_documents_permissions')}
        </p>
        <p>{t('v3:action_cannot_be_undone')}</p>
      </Modal>

      {/* Group Users Modal */}
      <Modal
        centered
        title={`Users in ${selectedGroupTitle}`}
        visible={groupUsersModalVisible}
        onCancel={handleCloseGroupUsersModal}
        footer={[
          <Button key="close" onClick={handleCloseGroupUsersModal}>
            {t('v3:close')}
          </Button>
        ]}
        width={500}
      >
        {selectedGroupUsers.length > 0 ? (
          <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
            {selectedGroupUsers.map((groupUser, index) => {
              const user = groupUser.user
              if (!user) return null

              const displayName =
                user.first_name && user.last_name
                  ? `${user.first_name} ${user.last_name}`
                  : user.first_name || user.last_name || user.name || user.email

              const avatarUrl =
                user.avatar && user.avatar.link ? user.avatar.link : null

              return (
                <div
                  key={user.id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 0',
                    borderBottom:
                      index < selectedGroupUsers.length - 1
                        ? '1px solid #f0f0f0'
                        : 'none'
                  }}
                >
                  <Avatar
                    style={{
                      background: '#eee',
                      color: '#333',
                      marginRight: 12
                    }}
                    src={avatarUrl || undefined}
                  >
                    {!avatarUrl && displayName
                      ? displayName
                        .split(' ')
                        .map(n => n[0])
                        .join('')
                      : ''}
                  </Avatar>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, marginBottom: 2 }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {user.email}
                    </div>
                    {user.username && (
                      <div style={{ fontSize: '12px', color: '#999' }}>
                        @{user.username}
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '32px 0' }}>
            <Empty description={t('v5:no_users_found_in_this_group')} />
          </div>
        )}
      </Modal>
    </Modal>
  )
}

export default ManagePermissionsModal
